// ─── BattleViewer2D – Arena & Sprite Renderers ─────────────────────────────

import {
  Sprite,
  DamageFloat,
  CameraState,
  CANVAS_W, CANVAS_H, ARENA_INSET, ARENA_RADIUS,
  CIRCLE_RADIUS, HP_BAR_W, HP_BAR_H,
  TEAM_COLORS, WEAPON_ICONS, SHIELD_ICON,
  WEAPON_SVG_NAMES, SHIELD_SVG_NAME,
} from './types';

// ─── Arena Drawing ──────────────────────────────────────────────────────────

function drawFloorTexture(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  ctx.save();
  const floorGrad = ctx.createRadialGradient(x + w / 2, y + h / 2, 40, x + w / 2, y + h / 2, w * 0.7);
  floorGrad.addColorStop(0, '#332f28');
  floorGrad.addColorStop(1, '#24221d');
  ctx.fillStyle = floorGrad;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, ARENA_RADIUS);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255,255,255,0.025)';
  ctx.lineWidth = 0.5;
  const tileSize = 48;
  for (let tx = x + tileSize; tx < x + w; tx += tileSize) {
    ctx.beginPath(); ctx.moveTo(tx, y + 6); ctx.lineTo(tx, y + h - 6); ctx.stroke();
  }
  for (let ty = y + tileSize; ty < y + h; ty += tileSize) {
    ctx.beginPath(); ctx.moveTo(x + 6, ty); ctx.lineTo(x + w - 6, ty); ctx.stroke();
  }
  ctx.restore();
}

function drawVignette(ctx: CanvasRenderingContext2D, flicker: number) {
  const vignette = ctx.createRadialGradient(
    CANVAS_W / 2, CANVAS_H / 2, CANVAS_W * 0.25,
    CANVAS_W / 2, CANVAS_H / 2, CANVAS_W * 0.65,
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  const glowAlpha = 0.04 + flicker * 0.02;
  const corners = [
    [ARENA_INSET + 14, ARENA_INSET + 14],
    [CANVAS_W - ARENA_INSET - 14, ARENA_INSET + 14],
    [ARENA_INSET + 14, CANVAS_H - ARENA_INSET - 14],
    [CANVAS_W - ARENA_INSET - 14, CANVAS_H - ARENA_INSET - 14],
  ];
  for (const [gx, gy] of corners) {
    const glow = ctx.createRadialGradient(gx, gy, 0, gx, gy, 110);
    glow.addColorStop(0, `rgba(255,160,40,${glowAlpha})`);
    glow.addColorStop(1, 'rgba(255,160,40,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }
}

function drawTorches(ctx: CanvasRenderingContext2D, time: number) {
  const corners = [
    [ARENA_INSET + 8, ARENA_INSET + 8],
    [CANVAS_W - ARENA_INSET - 8, ARENA_INSET + 8],
    [ARENA_INSET + 8, CANVAS_H - ARENA_INSET - 8],
    [CANVAS_W - ARENA_INSET - 8, CANVAS_H - ARENA_INSET - 8],
  ];
  for (const [tx, ty] of corners) {
    ctx.fillStyle = '#3a3020';
    ctx.fillRect(tx - 4, ty - 3, 8, 6);
    const flicker = Math.sin(time * 6 + tx) * 0.3 + 0.7;
    const flameH = 7 + flicker * 3;
    const flameGrad = ctx.createRadialGradient(tx, ty - flameH / 2, 0, tx, ty - flameH / 2, 6);
    flameGrad.addColorStop(0, `rgba(255,180,50,${0.7 * flicker})`);
    flameGrad.addColorStop(0.5, `rgba(255,100,20,${0.4 * flicker})`);
    flameGrad.addColorStop(1, 'rgba(255,60,10,0)');
    ctx.fillStyle = flameGrad;
    ctx.beginPath();
    ctx.ellipse(tx, ty - flameH / 2, 5, flameH / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function drawArena(ctx: CanvasRenderingContext2D, time: number, borderColor?: string) {
  const x = ARENA_INSET;
  const y = ARENA_INSET;
  const w = CANVAS_W - ARENA_INSET * 2;
  const h = CANVAS_H - ARENA_INSET * 2;

  drawFloorTexture(ctx, x, y, w, h);

  // Centre divider
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.05)';
  ctx.lineWidth = 1;
  ctx.setLineDash([6, 8]);
  ctx.beginPath();
  ctx.moveTo(CANVAS_W / 2, y + 10);
  ctx.lineTo(CANVAS_W / 2, y + h - 10);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // Wall border — use theme color if provided
  const bc = borderColor || '#5c4a2e';
  const grad = ctx.createLinearGradient(x, y, x + w, y + h);
  grad.addColorStop(0, bc);
  grad.addColorStop(1, darkenHex(bc, 0.5));
  ctx.strokeStyle = grad;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, ARENA_RADIUS);
  ctx.stroke();

  ctx.strokeStyle = '#111';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(x - 2, y - 2, w + 4, h + 4, ARENA_RADIUS + 2);
  ctx.stroke();

  drawTorches(ctx, time);
  drawVignette(ctx, Math.sin(time * 4) * 0.5 + 0.5);
}

/** Darken a hex color by a factor (0 = unchanged, 1 = black) */
function darkenHex(hex: string, factor: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = Math.round(parseInt(m[1], 16) * (1 - factor));
  const g = Math.round(parseInt(m[2], 16) * (1 - factor));
  const b = Math.round(parseInt(m[3], 16) * (1 - factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ─── Round Badge (drawn on canvas for proper scaling) ───────────────────────

export function drawRoundBadge(ctx: CanvasRenderingContext2D, roundNum: number) {
  if (roundNum <= 0) return;
  ctx.save();

  const text = `ROUND ${roundNum}`;
  ctx.font = "bold 11px 'Cinzel', sans-serif";
  const metrics = ctx.measureText(text);
  const textW = metrics.width;
  const padX = 18;
  const badgeH = 20;
  const badgeW = textW + padX * 2;
  const badgeX = (CANVAS_W - badgeW) / 2;
  const badgeY = 4;

  // Background pill
  ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 4);
  ctx.fill();

  // Border
  ctx.strokeStyle = 'rgba(45, 197, 162, 0.45)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 4);
  ctx.stroke();

  // Text
  ctx.fillStyle = '#2dc5a2';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, CANVAS_W / 2, badgeY + badgeH / 2 + 1);

  ctx.restore();
}

// ─── Team HP Summary Bars (below round badge) ──────────────────────────────

export function drawTeamHPBars(ctx: CanvasRenderingContext2D, sprites: Map<string, Sprite>) {
  const teamTotals: Record<string, { hp: number; maxHp: number }> = {};
  sprites.forEach(s => {
    if (!teamTotals[s.team]) teamTotals[s.team] = { hp: 0, maxHp: 0 };
    teamTotals[s.team].hp += Math.max(0, s.hp);
    teamTotals[s.team].maxHp += s.maxHp;
  });

  const teams = Object.keys(teamTotals);
  if (teams.length < 2) return;

  const barW = 120;
  const barH = 6;
  const barY = 28; // Below the round badge
  const gap = 20;
  const totalWidth = teams.length * barW + (teams.length - 1) * gap;
  let startX = (CANVAS_W - totalWidth) / 2;

  for (const team of teams) {
    const { hp, maxHp } = teamTotals[team];
    const frac = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
    const color = TEAM_COLORS[team] ?? '#888';

    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.beginPath(); ctx.roundRect(startX, barY, barW, barH, 3); ctx.fill();

    ctx.fillStyle = color;
    ctx.beginPath(); ctx.roundRect(startX, barY, Math.max(0, barW * frac), barH, 3); ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.roundRect(startX, barY, barW, barH, 3); ctx.stroke();

    startX += barW + gap;
  }
}

// ─── Sprite Drawing ─────────────────────────────────────────────────────────

// ─── SVG icon tinting helper ────────────────────────────────────────────────
function getTintedSvg(
  svgCache: Map<string, HTMLImageElement>,
  tintedCache: Map<string, HTMLCanvasElement>,
  name: string,
  color: string,
): HTMLCanvasElement | null {
  const cacheKey = `${name}_${color}`;
  if (tintedCache.has(cacheKey)) return tintedCache.get(cacheKey)!;
  const img = svgCache.get(name);
  if (!img || !img.complete || img.naturalWidth === 0) return null;
  const size = 64;
  const oc = document.createElement('canvas');
  oc.width = size;
  oc.height = size;
  const oc2d = oc.getContext('2d');
  if (!oc2d) return null;
  oc2d.drawImage(img, 0, 0, size, size);
  oc2d.globalCompositeOperation = 'source-in';
  oc2d.fillStyle = color;
  oc2d.fillRect(0, 0, size, size);
  tintedCache.set(cacheKey, oc);
  return oc;
}

export function drawSprite(
  ctx: CanvasRenderingContext2D,
  s: Sprite,
  imageCache: Map<string, HTMLImageElement>,
  svgCache?: Map<string, HTMLImageElement>,
  tintedCache?: Map<string, HTMLCanvasElement>,
  themeAccent?: string,
) {
  ctx.save();
  ctx.globalAlpha = Math.max(0, s.opacity);

  const cx = Math.round(s.x + s.shakeX);
  const cy = Math.round(s.y + s.shakeY);
  const scale = Math.max(0.3, s.scale); // Safety floor
  const r = CIRCLE_RADIUS * scale;
  const teamColor = TEAM_COLORS[s.team] ?? '#888';

  if (s.rotation !== 0) {
    ctx.translate(cx, cy);
    ctx.rotate(s.rotation);
    ctx.translate(-cx, -cy);
  }

  // Elliptical ground shadow
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.3)';
  ctx.beginPath();
  ctx.ellipse(cx, cy + r + 3, r * 0.9, Math.max(1, r * 0.3), 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Aura glow (safety: only if auraColor is truthy)
  if (s.auraTimer > 0 && s.auraColor) {
    ctx.save();
    const auraAlpha = Math.min(0.35, s.auraTimer * 0.25);
    try {
      const auraGrad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, r * 2);
      auraGrad.addColorStop(0, s.auraColor.replace(')', `,${auraAlpha})`).replace('rgb(', 'rgba('));
      auraGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = auraGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, r * 2, 0, Math.PI * 2);
      ctx.fill();
    } catch { /* invalid color, skip */ }
    ctx.restore();
  }

  // Resolve avatar image
  const avatarKey = s.race && s.sex && s.avatarNum > 0
    ? `${s.race.toLowerCase()}.${s.sex.toLowerCase()}.${s.avatarNum}`
    : null;
  const img = avatarKey ? imageCache.get(avatarKey) : null;
  const hasAvatar = !!(img && img.complete && img.naturalWidth > 0);

  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = 8;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = teamColor; ctx.fill();
  ctx.shadowBlur = 0;

  if (hasAvatar) {
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
    ctx.drawImage(img!, cx - r, cy - r, r * 2, r * 2);
    ctx.restore();
  }

  // Hit flash overlay
  if (s.flashTimer > 0) {
    const alpha = Math.min(0.65, s.flashTimer * 0.45);
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(200, 0, 0, ${alpha})`; ctx.fill();
  }

  // Outline ring
  if (s.state === 'surrendered' || s.state === 'collapsed') {
    ctx.setLineDash([4, 4]); ctx.strokeStyle = '#ddd'; ctx.lineWidth = 2;
  } else if (s.state === 'dead') {
    ctx.strokeStyle = '#333'; ctx.lineWidth = 2;
  } else {
    ctx.strokeStyle = hasAvatar ? teamColor : 'rgba(255,255,255,0.3)';
    ctx.lineWidth = hasAvatar ? 3 : 2;
  }
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  ctx.setLineDash([]);

  // Name label
  const displayName = s.name.length > 11 ? s.name.slice(0, 10) + '…' : s.name;
  ctx.font = 'bold 10px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  const nameY = cy - r - 4;
  ctx.strokeStyle = 'rgba(0,0,0,0.92)';
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.strokeText(displayName, cx, nameY);
  ctx.fillStyle = s.state === 'dead' ? '#555' : s.isLegend ? '#ffd700' : '#fff';
  ctx.fillText(displayName, cx, nameY);

  // Weapon type icons — positioned at the sides of the sprite
  if (s.state !== 'dead') {
    const isLeftTeam = s.team === 'teamOne' || s.team === 'teamThree';
    // Main hand faces the centre of the battle, offhand faces the back
    const mainHandX = isLeftTeam ? cx + r + 5 : cx - r - 5;
    const offHandX  = isLeftTeam ? cx - r - 5 : cx + r + 5;
    const iconY = cy + r - 2;
    const iconSize = 14;
    const accent = themeAccent || '#6ea8d0';

    const mainSvgName = s.weaponType ? (WEAPON_SVG_NAMES[s.weaponType] ?? null) : null;
    const offSvgName  = s.hasShield
      ? SHIELD_SVG_NAME
      : (s.offHandType ? (WEAPON_SVG_NAMES[s.offHandType] ?? null) : null);
    const mainEmoji = s.weaponType && WEAPON_ICONS[s.weaponType] ? WEAPON_ICONS[s.weaponType] : null;
    const offEmoji  = s.hasShield
      ? SHIELD_ICON
      : (s.offHandType && WEAPON_ICONS[s.offHandType] ? WEAPON_ICONS[s.offHandType] : null);

    const drawIcon = (svgName: string | null, emoji: string | null, handX: number, isLeft: boolean): boolean => {
      if (!svgName && !emoji) return false;
      if (svgName && svgCache && tintedCache) {
        const tinted = getTintedSvg(svgCache, tintedCache, svgName, accent);
        if (tinted) {
          const dx = isLeft ? handX : handX - iconSize;
          const iconCx = dx + iconSize / 2;
          ctx.save();
          ctx.translate(iconCx, iconY);
          if (!isLeft) ctx.scale(-1, 1); // flip to face arena centre
          ctx.drawImage(tinted, -iconSize / 2, -iconSize / 2, iconSize, iconSize);
          ctx.restore();
          return true;
        }
      }
      if (emoji) {
        ctx.font = `${iconSize}px sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.textAlign = isLeft ? 'left' : 'right';
        ctx.fillText(emoji, handX, iconY);
        return true;
      }
      return false;
    };

    const mainDrawn = drawIcon(mainSvgName, mainEmoji, mainHandX, isLeftTeam);
    if (mainDrawn && s.weaponBroken) {
      drawBrokenX(ctx, mainHandX + (isLeftTeam ? iconSize / 2 : -iconSize / 2), iconY, iconSize);
    }
    const offDrawn = drawIcon(offSvgName, offEmoji, offHandX, !isLeftTeam);
    if (offDrawn && (s.offHandBroken || s.shieldBroken)) {
      drawBrokenX(ctx, offHandX + (isLeftTeam ? -iconSize / 2 : iconSize / 2), iconY, iconSize);
    }
  }

  // HP bar below circle
  if (s.state !== 'dead' && s.maxHp > 0) {
    const barX = cx - HP_BAR_W / 2;
    const barY = cy + r + 6;
    const hpFrac = Math.max(0, Math.min(1, s.hp / s.maxHp));
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(barX, barY, HP_BAR_W, HP_BAR_H);
    ctx.fillStyle = hpFrac > 0.5 ? '#4caf50' : hpFrac > 0.25 ? '#ff9800' : '#f44336';
    ctx.fillRect(barX, barY, Math.floor(HP_BAR_W * hpFrac), HP_BAR_H);
  }

  ctx.restore();
}

function buildWeaponLabel(s: Sprite): string {
  const parts: string[] = [];
  if (s.weaponType && WEAPON_ICONS[s.weaponType]) parts.push(WEAPON_ICONS[s.weaponType]);
  if (s.hasShield) parts.push(SHIELD_ICON);
  else if (s.offHandType && WEAPON_ICONS[s.offHandType]) parts.push(WEAPON_ICONS[s.offHandType]);
  return parts.join(' ');
}

/** Draw a red X to indicate a broken weapon */
function drawBrokenX(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  ctx.save();
  ctx.strokeStyle = '#ff2222';
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  const half = size * 0.45;
  ctx.beginPath();
  ctx.moveTo(cx - half, cy - half); ctx.lineTo(cx + half, cy + half);
  ctx.moveTo(cx + half, cy - half); ctx.lineTo(cx - half, cy + half);
  ctx.stroke();
  ctx.restore();
}

// ─── Damage Floats ──────────────────────────────────────────────────────────

export function drawDamageFloats(ctx: CanvasRenderingContext2D, floats: DamageFloat[]) {
  for (const f of floats) {
    ctx.save();
    ctx.globalAlpha = Math.max(0, f.opacity);
    const size = f.fontSize ?? 17;
    ctx.font = `bold ${size}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0,0,0,0.97)';
    ctx.shadowBlur = 7;
    ctx.fillStyle = f.color;
    ctx.fillText(f.text, f.x, f.y);
    if (f.subText) {
      ctx.font = '9px sans-serif';
      ctx.globalAlpha = Math.max(0, f.opacity * 0.7);
      ctx.fillStyle = '#ccc';
      ctx.fillText(f.subText, f.x, f.y + size * 0.7);
    }
    ctx.restore();
  }
}

// ─── Round Transition Overlay (cinematic flash) ─────────────────────────────

export function drawRoundTransition(
  ctx: CanvasRenderingContext2D,
  roundNum: number,
  alpha: number,
) {
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  ctx.font = "bold 36px 'Cinzel', serif";
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffd700';
  ctx.shadowColor = '#ffd700';
  ctx.shadowBlur = 20;
  ctx.fillText(`ROUND ${roundNum}`, CANVAS_W / 2, CANVAS_H / 2);
  ctx.restore();
}

// ─── Narration Bar ──────────────────────────────────────────────────────────

export function drawNarrationBar(
  ctx: CanvasRenderingContext2D,
  text: string,
  displayedChars: number,
  alpha: number,
) {
  if (!text || alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  const barH = 36;
  const barY = CANVAS_H - barH;
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  ctx.fillRect(0, barY, CANVAS_W, barH);
  ctx.strokeStyle = 'rgba(255,200,80,0.3)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, barY); ctx.lineTo(CANVAS_W, barY); ctx.stroke();
  const shown = text.slice(0, Math.floor(displayedChars));
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#e0d8c0';
  ctx.fillText(shown, 16, barY + barH / 2, CANVAS_W - 32);
  ctx.restore();
}

// ─── Camera Transform Helpers ───────────────────────────────────────────────

export function applyCameraTransform(ctx: CanvasRenderingContext2D, cam: CameraState) {
  const cx = CANVAS_W / 2;
  const cy = CANVAS_H / 2;
  ctx.translate(cx, cy);
  ctx.scale(cam.zoom, cam.zoom);
  ctx.translate(-cx + cam.offsetX + cam.shakeX, -cy + cam.offsetY + cam.shakeY);
}

export function resetCameraTransform(ctx: CanvasRenderingContext2D) {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
}
